import React, { type ReactNode } from "react";
import { Model, GQL, KontoNumUtils, Utils } from "@binale-tech/shared";
import { FormattedMessage } from "react-intl";

import type { Category } from "@domain/models/Category";
import { CategoryUtils } from "@domain/models/utils/CategoryUtils";
import { type IExtraData } from "@domain/models/Interfaces";
import { type ProductKey, ProductKeys } from "@domain/models/Product";
import { logger } from "@domain/infrastructure/logger";
import { BinaleError } from "@domain/errors/errors";

export class TableProperties {
    static readonly ComputedNr = "Nr.";
    static readonly ControlLog = "Log";
    static readonly ControlAction = "Aktion";
    static readonly ComputedModul = "Modul";

    static permanentColumns() {
        return [this.ComputedNr, this.ControlAction];
    }
}

export class GenericRecordProperties {
    // Must be unique names!!! These values are the AG Grid colIds and are PERSISTED - the
    // per-user hidden-column list in Firebase (settings/{companyId}/user/{userId}/tables/{view})
    // and the column widths in localStorage both key off them. Renaming one silently orphans a
    // user's saved settings; change the registry's `headerName` to relabel a column instead.
    static readonly RecordDatum = "Datum";
    static readonly RecordPeriod = "Periode";
    static readonly RecordCategoryCreditorNum = "Konto";
    static readonly RecordCategoryCreditorName = "Konto Bezeichnung";
    static readonly RecordBelegfeld1 = "Rechnung Nr.";
    static readonly RecordBrutto = "Brutto";
    static readonly RecordCurrencyOriginalAmount = "Eingabebetrag";
    static readonly RecordCurrencyRate = "Kurs";
    static readonly RecordCurrencyCode = "WKZ";
    static readonly RecordDocuments = "Dokumente";
    static readonly RecordBankingTransactionId = "Transaktion ID";
    static readonly RecordCashRegisterTransactionId = "Kassenbeleg ID";
    static readonly RecordNotes = "Notes";
    // Persistence key, never rename: see the class comment.
    static readonly RecordUStVA = "UStVA";
    static readonly RecordXRechnung = "E-Rechnung";
    static readonly RecordFalligkeit = "Fälligkeit";
    static readonly RecordReview = "Kontrolle";
    static readonly RecordAiReview = "KI-Kontrolle";
    static readonly RecordContact = "Contact";

    static readonly ItemCategoryCreditorNum = "G.Konto";
    static readonly ItemCategoryCreditorName = "G.Konto Bezeichnung";
    static readonly ItemBelegfeld1 = "Belegfeld 1";
    static readonly ItemBelegfeld2 = "Belegfeld 2";
    static readonly ItemBuchungstext = "Ware/Leistung";
    static readonly ItemBuchungstext2 = "Zusatz-Info";
    static readonly ItemUStPerc = "USt, %";
    static readonly ItemKS = "KS";
    static readonly ItemUSt13b = "13b";

    static readonly ComputedNetto = "Netto";
    static readonly ComputedUSt = "USt";
    static readonly ComputedOffen = "offen";
    static readonly ComputedSaldo = "Saldo";
    static readonly ComputedKASaldoBrutto = "Saldo (Brutto)";
    static readonly ComputedKABruttoSoll = "Soll (Brutto)";
    static readonly ComputedKABruttoHaben = "Haben (Brutto)";
    static readonly ComputedKASoll = "Soll";
    static readonly ComputedKAHaben = "Haben";
    static readonly ComputedZA = "ZA";
    static readonly ComputedStatus = "Status";
    static readonly ComputedZahlung = "Zahlungen";
    static readonly ComputedSkonto = "Skonto";
    static readonly ComputedBu = "BU";

    static readonly ComputedSHInfo = "Info";
    static readonly ControlPaymentConnection = "Binding";

    // Export-only columns (FIBU-443). Never part of a product's tableColumns(), so they never
    // reach the grid, the column picker or the persisted per-user column settings - a view opts
    // in via TableViewDescriptor.exportOnlyColumns / exportGroupByColumns.
    static readonly ExportContactBankDetails = "Bankverbindung";
    /** Group key only: contact, else the record's Kreditor/Debitor. Not an output column. */
    static readonly ExportPaymentGroup = "Zahlungsgruppe";

    /** DATEV import preview only: per-row problem indicator (FIBU-504). */
    static readonly ImportAnnotation = "Anmerkung";
}

export interface FormLabelsOverride {
    recordCategoryCreditor?: ReactNode;
}

export type CategoryCreditorMode =
    | "category"
    | "creditor"
    | "combined"
    | "debitor"
    | "resolver"
    | "modeRecordKontoFE"
    | "modeItemCatFE";

export class CategoryCreditorModes {
    static readonly CAT: CategoryCreditorMode = "category";
    static readonly CRED: CategoryCreditorMode = "creditor";
    static readonly DEB: CategoryCreditorMode = "debitor";
    static readonly CCD: CategoryCreditorMode = "combined";
    static readonly RESOLVER: CategoryCreditorMode = "resolver";
    static readonly RecordKontoFE: CategoryCreditorMode = "modeRecordKontoFE"; // CCD with disabled auto categories in case saldo category was selected in Konto,
    static readonly ItemCatFE: CategoryCreditorMode = "modeItemCatFE"; // CAT with disabled bank, kb
}

/**
 * Per-view capabilities for record-manipulation surfaces. The domain resolver
 * (GenericRecordUtils.getManipulationPermissions) intersects record-state permissions with these,
 * so a view can be read-only for the record form while still allowing delete/Storno/Avis. Defaults
 * are derived from `enableRecordForm` (home modules: all true; overviews: no form/copy, but
 * delete/cancel/avis allowed). Set explicitly only to deviate from that default profile.
 */
export interface RecordViewCapabilities {
    openForm: boolean; // may open the record form (edit) from this view
    copy: boolean; // may copy a record (opens the form) from this view
    delete: boolean; // may delete records from this view
    cancel: boolean; // may Storno records from this view
    avis: boolean; // may toggle Zahlungsavis from this view
}

export interface ProductConfig {
    enableRecordForm: boolean; // enabled in the main modules, disabled in the overviews
    recordViewCapabilities?: Partial<RecordViewCapabilities>; // override the enableRecordForm-derived default profile
    recordAccountMode: CategoryCreditorMode; // ER - creditor; KB, Bank - resolver; FE - modeRecordKontoFE
    recordAccountResolver?: (year: number, categories: Map<number, Category>, selectedRecordGroup: string) => Category; // KB, Bank;
    itemAccountMode: CategoryCreditorMode; // KB, Bank, FE - category, ER - category, ER_A - resolver
    itemAccountResolver?: (year: number, categories: Map<number, Category>) => Category; // KB, Bank - null;
    putRecordCategoryCreditorAfterUst?: boolean; // FE
    useLastschrift?: boolean; // ER
    useFalligkeit?: boolean; // ER
    useContact?: "afterDate" | "beforeSplit" | "afterBu"; // ER, Deb - afterDate | Bank, KB, LA - beforeSplit, FE/POS - afterBu
    useItemBelegfeld1?: boolean; // Bank, KB, FE, Pos -> Utils.ModuleUtils.useBelegfeld1Split
    useItemBelegfeld1Required?: boolean; // just for consistency with record
    isRecordBelegfeld1Required?: boolean; // ER, Deb, LA
    labels?: FormLabelsOverride;
    modalDefaultFocusCCField?: boolean; // FE
    useSplit?: boolean; // ER->Anzahlungen, LA
    usePaymentBinding?: "beforeBu" | "afterBu"; // Bank, KB - before, FE - after
    useItemAccount?: boolean; // ER->Anzahlungen
    itemBuResolver?: (formBu: Model.Bu) => { bu: Model.Bu; extra: IExtraData | null }; // ER->Anzahlungen / LA
    disableBu?: boolean; // LA
    useSollHabenHints?: boolean; // FE, LA, KB, Bank
    disableTableSorting?: boolean; // Bank, KB
    usePriorityOrder?: boolean; // KB
    disableNegativeJournalSaldo?: boolean; // KB
    bulkEditFields?: string[];
    bulkEditSplitFields?: string[]; // ER, ER_A, Deb - Kontakt + Kreditor/Debitor; default: Kontakt only
}

export interface TableColumnNameOverride {
    label?: string;
    header?: string | ReactNode;
}

export interface ProductColumnDataProvider {
    productKey(): ProductKey;

    tableColumnOverrides(): Map<string, TableColumnNameOverride>;

    nonEditableTableColumns(): Set<string>;

    tableColumns(): string[];

    useAbsAmounts(): boolean;
}

const emptySet = new Set<string>();
const emptyMap = new Map();

export abstract class Product implements ProductColumnDataProvider {
    constructor(
        protected skr: number,
        protected company: GQL.ICompany,
    ) {}
    abstract tableColumns(): string[];

    abstract getConfig(): ProductConfig;

    abstract productKey(): ProductKey;

    tableColumnOverrides(): Map<string, TableColumnNameOverride> {
        return emptyMap;
    }

    nonEditableTableColumns(): Set<string> {
        return emptySet;
    }

    useAbsAmounts(): boolean {
        return false;
    }
}

const BulkEditRecordSourceFields = Object.freeze([
    GenericRecordProperties.RecordContact,
    GenericRecordProperties.ItemBelegfeld2,
    GenericRecordProperties.RecordBelegfeld1,
    GenericRecordProperties.ItemKS,
    GenericRecordProperties.RecordCategoryCreditorNum,
    GenericRecordProperties.ItemCategoryCreditorNum,
    GenericRecordProperties.ItemUStPerc,
    GenericRecordProperties.ItemBuchungstext,
    GenericRecordProperties.ItemBuchungstext2,
]);
const BulkEditRecordRepresentationFields = Object.freeze([
    GenericRecordProperties.RecordContact,
    GenericRecordProperties.ItemBelegfeld1,
    GenericRecordProperties.ItemBelegfeld2,
    GenericRecordProperties.ItemKS,
    GenericRecordProperties.ItemCategoryCreditorNum,
    GenericRecordProperties.ItemUStPerc,
    GenericRecordProperties.ItemBuchungstext,
    GenericRecordProperties.ItemBuchungstext2,
]);
const BulkEditRecordRepresentationEFFields = Object.freeze([
    GenericRecordProperties.RecordContact,
    GenericRecordProperties.ItemBelegfeld1,
    GenericRecordProperties.ItemBelegfeld2,
    GenericRecordProperties.ItemKS,
    GenericRecordProperties.RecordCategoryCreditorNum, // this is the difference from the general representation
    GenericRecordProperties.ItemCategoryCreditorNum,
    GenericRecordProperties.ItemUStPerc,
    GenericRecordProperties.ItemBuchungstext,
    GenericRecordProperties.ItemBuchungstext2,
]);
// FIBU-965: rows that survive a selection containing a split record. Only record-level fields
// qualify - an item-level value has no single item to address across a split.
export const BulkEditSplitFieldsDefault = Object.freeze([GenericRecordProperties.RecordContact]);
const BulkEditSplitFieldsSource = Object.freeze([
    GenericRecordProperties.RecordContact,
    GenericRecordProperties.RecordCategoryCreditorNum,
]);

export class ProductER extends Product {
    productKey() {
        return ProductKeys.ER;
    }

    getConfig(): ProductConfig {
        return {
            enableRecordForm: true,
            recordAccountMode: CategoryCreditorModes.CRED,
            itemAccountMode: CategoryCreditorModes.CAT,
            labels: { recordCategoryCreditor: <FormattedMessage id="app.fields.creditor" /> },
            useLastschrift: true,
            useFalligkeit: true,
            useContact: "afterDate",
            isRecordBelegfeld1Required: true,
            useSplit: true,
            useItemAccount: true,
            bulkEditFields: [...BulkEditRecordSourceFields],
            bulkEditSplitFields: [...BulkEditSplitFieldsSource],
        };
    }

    tableColumnOverrides() {
        const m = new Map();
        m.set(GenericRecordProperties.RecordCategoryCreditorNum, {
            header: <FormattedMessage id="app.fields.creditor" />,
        });
        return m;
    }

    tableColumns() {
        return [
            TableProperties.ComputedNr,
            GenericRecordProperties.RecordPeriod,
            GenericRecordProperties.RecordDatum,
            GenericRecordProperties.RecordContact,
            GenericRecordProperties.RecordCategoryCreditorNum,
            GenericRecordProperties.RecordCategoryCreditorName,
            GenericRecordProperties.RecordBelegfeld1,
            GenericRecordProperties.ItemBelegfeld2,
            GenericRecordProperties.ItemCategoryCreditorNum,
            GenericRecordProperties.ItemCategoryCreditorName,
            GenericRecordProperties.RecordBrutto,
            GenericRecordProperties.RecordCurrencyCode,
            GenericRecordProperties.RecordCurrencyOriginalAmount,
            GenericRecordProperties.RecordCurrencyRate,
            GenericRecordProperties.ItemUStPerc,
            GenericRecordProperties.ComputedBu,
            GenericRecordProperties.ItemUSt13b,
            GenericRecordProperties.ComputedUSt,
            GenericRecordProperties.ComputedNetto,
            GenericRecordProperties.ComputedOffen,
            GenericRecordProperties.ComputedZA,
            GenericRecordProperties.ComputedStatus,
            GenericRecordProperties.ItemKS,
            GenericRecordProperties.RecordFalligkeit,
            GenericRecordProperties.ItemBuchungstext,
            GenericRecordProperties.ItemBuchungstext2,
            GenericRecordProperties.RecordDocuments,
            GenericRecordProperties.RecordXRechnung,
            GenericRecordProperties.RecordNotes,
            GenericRecordProperties.RecordUStVA,
            TableProperties.ControlAction,
            GenericRecordProperties.RecordReview,
            GenericRecordProperties.RecordAiReview,
            TableProperties.ControlLog,
            GenericRecordProperties.ComputedSHInfo,
        ];
    }
}

export class ProductERAnzahlungen extends ProductER {
    productKey() {
        return ProductKeys.ER_A;
    }

    getConfig(): ProductConfig {
        const skr = this.skr;
        return {
            ...super.getConfig(),
            useSplit: false,
            useItemAccount: false,
            itemAccountMode: CategoryCreditorModes.RESOLVER,
            itemAccountResolver: (year: number, categories: Map<number, Category>) => {
                switch (skr) {
                    case 3:
                        return categories.get(1793);
                    case 4:
                        return categories.get(3695);
                }
                return null;
            },
            itemBuResolver: (formBu: Model.Bu) => ({ bu: Model.Bu.KU, extra: { bu: formBu } }),
        };
    }
}

abstract class ProductAuswertungFalligkeit extends ProductER {
    getConfig(): ProductConfig {
        return {
            ...super.getConfig(),
            enableRecordForm: false,
        };
    }
    tableColumns() {
        return [
            TableProperties.ComputedNr,
            GenericRecordProperties.RecordFalligkeit,
            GenericRecordProperties.RecordPeriod,
            GenericRecordProperties.RecordDatum,
            GenericRecordProperties.RecordContact,
            GenericRecordProperties.RecordCategoryCreditorNum,
            GenericRecordProperties.RecordCategoryCreditorName,
            GenericRecordProperties.RecordBelegfeld1,
            GenericRecordProperties.ItemBelegfeld2,
            GenericRecordProperties.ItemCategoryCreditorNum,
            GenericRecordProperties.ItemCategoryCreditorName,
            GenericRecordProperties.RecordBrutto,
            GenericRecordProperties.RecordCurrencyCode,
            GenericRecordProperties.RecordCurrencyOriginalAmount,
            GenericRecordProperties.RecordCurrencyRate,
            GenericRecordProperties.ItemUStPerc,
            GenericRecordProperties.ComputedUSt,
            GenericRecordProperties.ItemUSt13b,
            GenericRecordProperties.ComputedNetto,
            GenericRecordProperties.ComputedOffen,
            GenericRecordProperties.ComputedZA,
            GenericRecordProperties.ComputedStatus,
            GenericRecordProperties.ItemKS,
            GenericRecordProperties.ItemBuchungstext,
            GenericRecordProperties.ItemBuchungstext2,
            GenericRecordProperties.RecordDocuments,
            GenericRecordProperties.RecordXRechnung,
            GenericRecordProperties.RecordNotes,
            GenericRecordProperties.RecordUStVA,
            TableProperties.ControlAction,
            TableProperties.ControlLog,
        ];
    }
}
export class ProductAuswertungFalligkeitER extends ProductAuswertungFalligkeit {
    productKey() {
        return ProductKeys.ER;
    }
}
export class ProductAuswertungFalligkeitERAnz extends ProductAuswertungFalligkeit {
    productKey() {
        return ProductKeys.ER_A;
    }
}
const OposAvisColumns = [
    TableProperties.ComputedNr,
    GenericRecordProperties.RecordPeriod,
    GenericRecordProperties.RecordDatum,
    GenericRecordProperties.RecordContact,
    GenericRecordProperties.RecordCategoryCreditorNum,
    GenericRecordProperties.RecordCategoryCreditorName,
    GenericRecordProperties.RecordBelegfeld1,
    GenericRecordProperties.ItemBelegfeld2,
    GenericRecordProperties.ItemCategoryCreditorNum,
    GenericRecordProperties.ItemCategoryCreditorName,
    GenericRecordProperties.RecordBrutto,
    GenericRecordProperties.RecordCurrencyCode,
    GenericRecordProperties.RecordCurrencyOriginalAmount,
    GenericRecordProperties.RecordCurrencyRate,
    GenericRecordProperties.ItemUStPerc,
    GenericRecordProperties.ComputedBu,
    GenericRecordProperties.ItemUSt13b,
    GenericRecordProperties.ComputedUSt,
    GenericRecordProperties.ComputedNetto,
    GenericRecordProperties.ComputedOffen,
    GenericRecordProperties.ComputedZA,
    GenericRecordProperties.ComputedStatus,
    GenericRecordProperties.ItemKS,
    GenericRecordProperties.RecordFalligkeit,
    GenericRecordProperties.ItemBuchungstext,
    GenericRecordProperties.ItemBuchungstext2,
    GenericRecordProperties.RecordDocuments,
    GenericRecordProperties.RecordXRechnung,
    GenericRecordProperties.RecordNotes,
    GenericRecordProperties.RecordUStVA,
    GenericRecordProperties.RecordReview,
    GenericRecordProperties.RecordAiReview,
    TableProperties.ControlLog,
    GenericRecordProperties.ComputedSHInfo,
];

class ProductAuswertungOposAvis extends ProductER {
    getConfig(): ProductConfig {
        return {
            ...super.getConfig(),
            enableRecordForm: false,
        };
    }
    tableColumns() {
        return OposAvisColumns;
    }
}
export class ProductAuswertungOposAvisER extends ProductAuswertungOposAvis {
    productKey() {
        return ProductKeys.ER;
    }
}
export class ProductAuswertungOposAvisERAnz extends ProductAuswertungOposAvis {
    productKey() {
        return ProductKeys.ER_A;
    }
}

export class ProductDeb extends Product {
    productKey() {
        return ProductKeys.Deb;
    }

    getConfig(): ProductConfig {
        return {
            enableRecordForm: true,
            recordAccountMode: CategoryCreditorModes.DEB,
            itemAccountMode: CategoryCreditorModes.CAT,
            useLastschrift: true,
            useFalligkeit: true,
            isRecordBelegfeld1Required: true,
            labels: { recordCategoryCreditor: <FormattedMessage id="app.fields.debitor" /> },
            useSplit: true,
            useContact: "afterDate",
            useItemAccount: true,
            bulkEditFields: [...BulkEditRecordSourceFields],
            bulkEditSplitFields: [...BulkEditSplitFieldsSource],
        };
    }

    tableColumnOverrides() {
        const m = new Map();
        m.set(GenericRecordProperties.RecordCategoryCreditorNum, {
            header: <FormattedMessage id="app.fields.debitor" />,
        });
        m.set(GenericRecordProperties.RecordCategoryCreditorName, {
            header: <FormattedMessage id="app.domain.debitorName" />,
        });
        return m;
    }

    tableColumns() {
        return [
            TableProperties.ComputedNr,
            GenericRecordProperties.RecordPeriod,
            GenericRecordProperties.RecordDatum,
            GenericRecordProperties.RecordContact,
            GenericRecordProperties.RecordCategoryCreditorNum,
            GenericRecordProperties.RecordCategoryCreditorName,
            GenericRecordProperties.RecordBelegfeld1,
            GenericRecordProperties.ItemBelegfeld2,
            GenericRecordProperties.ItemCategoryCreditorNum,
            GenericRecordProperties.ItemCategoryCreditorName,
            GenericRecordProperties.RecordBrutto,
            GenericRecordProperties.RecordCurrencyCode,
            GenericRecordProperties.RecordCurrencyOriginalAmount,
            GenericRecordProperties.RecordCurrencyRate,
            GenericRecordProperties.ItemUStPerc,
            GenericRecordProperties.ComputedBu,
            GenericRecordProperties.ItemUSt13b,
            GenericRecordProperties.ComputedUSt,
            GenericRecordProperties.ComputedNetto,
            GenericRecordProperties.ComputedOffen,
            GenericRecordProperties.ComputedZA,
            GenericRecordProperties.ComputedStatus,
            GenericRecordProperties.ItemKS,
            GenericRecordProperties.RecordFalligkeit,
            GenericRecordProperties.ItemBuchungstext,
            GenericRecordProperties.ItemBuchungstext2,
            GenericRecordProperties.RecordDocuments,
            GenericRecordProperties.RecordXRechnung,
            GenericRecordProperties.RecordNotes,
            GenericRecordProperties.RecordUStVA,
            TableProperties.ControlAction,
            GenericRecordProperties.RecordReview,
            GenericRecordProperties.RecordAiReview,
            TableProperties.ControlLog,
            GenericRecordProperties.ComputedSHInfo,
        ];
    }
}

export class ProductAuswertungOposAvisDeb extends ProductDeb {
    getConfig(): ProductConfig {
        return {
            ...super.getConfig(),
            enableRecordForm: false,
        };
    }
    tableColumns() {
        return OposAvisColumns;
    }
}

export class ProductFE extends Product {
    productKey() {
        return GQL.IProductKey.Fe;
    }
    static getSHHint(brutto: number) {
        if (brutto > 0) {
            return {
                konto: <code>S</code>,
                gkonto: <code>H</code>,
            };
        }
        if (brutto < 0) {
            return {
                konto: <code>H</code>,
                gkonto: <code>S</code>,
            };
        }
        return {
            konto: null,
            gkonto: null,
        };
    }

    useAbsAmounts(): boolean {
        return true;
    }

    getConfig(): ProductConfig {
        return {
            enableRecordForm: true,
            recordAccountMode: CategoryCreditorModes.RecordKontoFE,
            itemAccountMode: CategoryCreditorModes.ItemCatFE,
            putRecordCategoryCreditorAfterUst: true,
            modalDefaultFocusCCField: true,
            useSplit: true,
            useContact: "afterBu",
            useItemAccount: true,
            useSollHabenHints: true,
            usePaymentBinding: "afterBu",
            useItemBelegfeld1: Utils.ModuleUtils.useBelegfeld1Split(this.productKey()),
            bulkEditFields: [...BulkEditRecordRepresentationEFFields],
        };
    }

    tableColumnOverrides() {
        const m = new Map();

        return m;
    }

    tableColumns() {
        return [
            TableProperties.ComputedNr,
            GenericRecordProperties.RecordPeriod,
            GenericRecordProperties.RecordDatum,
            GenericRecordProperties.RecordBrutto,
            GenericRecordProperties.RecordCurrencyCode,
            GenericRecordProperties.RecordCurrencyOriginalAmount,
            GenericRecordProperties.RecordCurrencyRate,
            GenericRecordProperties.ItemUStPerc,
            GenericRecordProperties.ComputedBu,
            GenericRecordProperties.ItemUSt13b,
            GenericRecordProperties.ComputedUSt,
            GenericRecordProperties.ComputedNetto,
            GenericRecordProperties.ItemCategoryCreditorNum,
            GenericRecordProperties.ItemCategoryCreditorName,
            GenericRecordProperties.RecordBelegfeld1,
            GenericRecordProperties.ItemBelegfeld2,
            GenericRecordProperties.RecordContact,
            GenericRecordProperties.RecordCategoryCreditorNum,
            GenericRecordProperties.RecordCategoryCreditorName,
            GenericRecordProperties.ItemKS,
            GenericRecordProperties.ItemBuchungstext,
            GenericRecordProperties.ItemBuchungstext2,
            GenericRecordProperties.RecordDocuments,
            GenericRecordProperties.RecordXRechnung,
            GenericRecordProperties.ControlPaymentConnection,
            GenericRecordProperties.RecordNotes,
            GenericRecordProperties.RecordUStVA,
            TableProperties.ControlAction,
            GenericRecordProperties.RecordReview,
            GenericRecordProperties.RecordAiReview,
            TableProperties.ControlLog,
            GenericRecordProperties.ComputedSHInfo,
        ];
    }

    static getRecordFeKontoAttributes(disabled: boolean, itemKonto: Model.IExtNum) {
        const disableAutoCategories = !this.isItemKontoSaldo(itemKonto);
        disabled = disabled || !itemKonto;
        return { disabled, disableAutoCategories };
    }
    static isItemKontoSaldo(itemKonto: Model.IExtNum) {
        return itemKonto ? Utils.FeRules.isSaldoKonto(itemKonto.getExtNum(0)) : false;
    }
}

export class ProductPOS extends ProductFE {
    productKey() {
        return GQL.IProductKey.Pos;
    }

    getConfig(): ProductConfig {
        const config = super.getConfig();
        return {
            ...config,
            usePaymentBinding: null,
        };
    }
}

export class ProductLA extends Product {
    productKey() {
        return ProductKeys.LA;
    }

    getConfig(): ProductConfig {
        const skr = this.skr;
        return {
            enableRecordForm: true,
            putRecordCategoryCreditorAfterUst: true,
            itemAccountMode: CategoryCreditorModes.CCD,
            recordAccountMode: CategoryCreditorModes.RESOLVER,
            useContact: "beforeSplit",
            recordAccountResolver: (year: number, categories: Map<number, Category>) => {
                switch (skr) {
                    case 3:
                        return categories.get(KontoNumUtils.LA_3);
                    case 4:
                        return categories.get(KontoNumUtils.LA_4);
                }
                return null;
            },
            useSplit: false,
            itemBuResolver: () => ({ bu: Model.Bu.KU, extra: null }),
            // disableBu: true,
            useItemAccount: true,
            isRecordBelegfeld1Required: true,
            useSollHabenHints: true,
        };
    }

    tableColumnOverrides() {
        const m = new Map();
        return m;
    }

    tableColumns() {
        return [
            TableProperties.ComputedNr,
            GenericRecordProperties.RecordPeriod,
            GenericRecordProperties.RecordDatum,
            GenericRecordProperties.RecordContact,
            GenericRecordProperties.ItemCategoryCreditorNum,
            GenericRecordProperties.ItemCategoryCreditorName,
            GenericRecordProperties.RecordBelegfeld1,
            GenericRecordProperties.ItemBelegfeld2,
            GenericRecordProperties.RecordCategoryCreditorNum,
            GenericRecordProperties.RecordCategoryCreditorName,
            GenericRecordProperties.RecordBrutto,
            GenericRecordProperties.RecordCurrencyCode,
            GenericRecordProperties.RecordCurrencyOriginalAmount,
            GenericRecordProperties.RecordCurrencyRate,
            GenericRecordProperties.ItemUStPerc,
            GenericRecordProperties.ComputedUSt,
            GenericRecordProperties.ComputedNetto,
            GenericRecordProperties.ItemKS,
            GenericRecordProperties.ItemBuchungstext,
            GenericRecordProperties.ItemBuchungstext2,
            GenericRecordProperties.RecordDocuments,
            GenericRecordProperties.RecordXRechnung,
            GenericRecordProperties.RecordNotes,
            GenericRecordProperties.RecordUStVA,
            TableProperties.ControlAction,
            GenericRecordProperties.RecordReview,
            GenericRecordProperties.RecordAiReview,
            TableProperties.ControlLog,
            GenericRecordProperties.ComputedSHInfo,
        ];
    }
}

export class ProductKB extends Product {
    productKey() {
        return GQL.IProductKey.Kb;
    }

    getConfig(): ProductConfig {
        const kbs = this.company.kasseList;
        return {
            enableRecordForm: true,
            useItemBelegfeld1: Utils.ModuleUtils.useBelegfeld1Split(this.productKey()),
            itemAccountMode: CategoryCreditorModes.CCD,
            recordAccountMode: CategoryCreditorModes.RESOLVER,
            recordAccountResolver: (year: number, categories: Map<number, Category>, selectedRecordGroup: string) => {
                const kb = kbs.find((v) => v.id === selectedRecordGroup && v.year === year);
                if (!kb) {
                    throw new BinaleError(
                        "no data for selected kb: " + JSON.stringify([selectedRecordGroup, kbs]),
                        "app.error.message.kb_not_available",
                    );
                }
                if (!kb.accountNum) {
                    logger.error("kassenbush should be updated");
                    return null;
                }
                return CategoryUtils.enrichCategory({ num: String(kb.accountNum), name: kb.name }, categories);
            },
            useSplit: true,
            useContact: "beforeSplit",
            usePaymentBinding: "beforeBu",
            useItemAccount: true,
            disableTableSorting: true,
            usePriorityOrder: true,
            disableNegativeJournalSaldo: true,
            useSollHabenHints: true,
            bulkEditFields: [...BulkEditRecordRepresentationFields],
        };
    }

    tableColumnOverrides() {
        const m = new Map();
        return m;
    }

    tableColumns() {
        return [
            TableProperties.ComputedNr,
            GenericRecordProperties.RecordPeriod,
            GenericRecordProperties.RecordDatum,
            GenericRecordProperties.RecordBrutto,
            GenericRecordProperties.RecordCurrencyCode,
            GenericRecordProperties.RecordCurrencyOriginalAmount,
            GenericRecordProperties.RecordCurrencyRate,
            GenericRecordProperties.ComputedSaldo,
            GenericRecordProperties.RecordBelegfeld1,
            GenericRecordProperties.ItemBelegfeld2,
            GenericRecordProperties.RecordContact,
            GenericRecordProperties.ItemUStPerc,
            GenericRecordProperties.ComputedBu,
            GenericRecordProperties.ItemUSt13b,
            GenericRecordProperties.ComputedUSt,
            GenericRecordProperties.ComputedNetto,
            GenericRecordProperties.ItemCategoryCreditorNum,
            GenericRecordProperties.ItemCategoryCreditorName,
            GenericRecordProperties.ItemKS,
            GenericRecordProperties.ItemBuchungstext,
            GenericRecordProperties.ItemBuchungstext2,
            GenericRecordProperties.RecordDocuments,
            GenericRecordProperties.RecordXRechnung,
            GenericRecordProperties.RecordCashRegisterTransactionId,
            GenericRecordProperties.ControlPaymentConnection,
            GenericRecordProperties.RecordNotes,
            GenericRecordProperties.RecordUStVA,
            TableProperties.ControlAction,
            GenericRecordProperties.RecordReview,
            GenericRecordProperties.RecordAiReview,
            TableProperties.ControlLog,
            GenericRecordProperties.ComputedSHInfo,
        ];
    }
}

export class ProductBank extends Product {
    productKey() {
        return GQL.IProductKey.Bank;
    }

    getConfig(): ProductConfig {
        const banks = this.company.bankList;
        return {
            enableRecordForm: true,
            useItemBelegfeld1: Utils.ModuleUtils.useBelegfeld1Split(this.productKey()),
            itemAccountMode: CategoryCreditorModes.CCD,
            recordAccountMode: CategoryCreditorModes.RESOLVER,
            recordAccountResolver: (year: number, categories: Map<number, Category>, selectedRecordGroup: string) => {
                const bank = banks.find((v) => v.id === selectedRecordGroup && v.year === year);
                if (!bank) {
                    throw new BinaleError(
                        "no data for selected bank: " + JSON.stringify([selectedRecordGroup, banks]),
                        "app.error.message.bank_not_available",
                    );
                }
                if (!bank.accountNum) {
                    logger.error("bank should be updated");
                    return null;
                }
                return CategoryUtils.enrichCategory({ num: String(bank.accountNum), name: bank.name }, categories);
            },
            useSplit: true,
            useContact: "beforeSplit",
            usePaymentBinding: "beforeBu",
            useItemAccount: true,
            disableTableSorting: true,
            useSollHabenHints: true,
            bulkEditFields: [...BulkEditRecordRepresentationFields],
        };
    }

    tableColumnOverrides() {
        const m = new Map();
        return m;
    }

    tableColumns() {
        return [
            TableProperties.ComputedNr,
            GenericRecordProperties.RecordPeriod,
            GenericRecordProperties.RecordDatum,
            GenericRecordProperties.RecordBrutto,
            GenericRecordProperties.RecordCurrencyCode,
            GenericRecordProperties.RecordCurrencyOriginalAmount,
            GenericRecordProperties.RecordCurrencyRate,
            GenericRecordProperties.ComputedSaldo,
            GenericRecordProperties.RecordBelegfeld1,
            GenericRecordProperties.ItemBelegfeld2,
            GenericRecordProperties.ItemUStPerc,
            GenericRecordProperties.ComputedBu,
            GenericRecordProperties.ItemUSt13b,
            GenericRecordProperties.ComputedUSt,
            GenericRecordProperties.ComputedNetto,
            GenericRecordProperties.RecordContact,
            GenericRecordProperties.ItemCategoryCreditorNum,
            GenericRecordProperties.ItemCategoryCreditorName,
            GenericRecordProperties.ItemKS,
            GenericRecordProperties.ItemBuchungstext,
            GenericRecordProperties.ItemBuchungstext2,
            GenericRecordProperties.RecordDocuments,
            GenericRecordProperties.RecordXRechnung,
            GenericRecordProperties.RecordBankingTransactionId,
            GenericRecordProperties.ControlPaymentConnection,
            GenericRecordProperties.RecordNotes,
            GenericRecordProperties.RecordUStVA,
            TableProperties.ControlAction,
            GenericRecordProperties.RecordReview,
            GenericRecordProperties.RecordAiReview,
            TableProperties.ControlLog,
            GenericRecordProperties.ComputedSHInfo,
        ];
    }
}

abstract class ProductAggregation extends Product {
    productKey() {
        return ProductKeys.AccountingCommon;
    }

    getConfig(): ProductConfig {
        return {
            enableRecordForm: false,
            // In the common cross-product overviews (KontenAuskunft, CommonAuswertung) Delete and
            // Storno are reserved for the record's home module (product decision 2026-07-22). The
            // per-product Fälligkeit/OPOS/AVIS views keep the overview default profile.
            recordViewCapabilities: { delete: false, cancel: false },
            recordAccountMode: CategoryCreditorModes.CCD,
            itemAccountMode: CategoryCreditorModes.CCD,
            useSplit: true,
            useItemAccount: true,
        };
    }

    tableColumnOverrides() {
        const m = new Map();
        return m;
    }
}

export class ProductCommonKontenAuskunft extends ProductAggregation {
    tableColumns() {
        return [
            TableProperties.ComputedNr,
            GenericRecordProperties.RecordPeriod,
            GenericRecordProperties.RecordDatum,
            GenericRecordProperties.RecordContact,
            GenericRecordProperties.RecordCategoryCreditorNum,
            GenericRecordProperties.RecordCategoryCreditorName,

            GenericRecordProperties.ItemUStPerc,
            GenericRecordProperties.ComputedBu,
            GenericRecordProperties.ItemUSt13b,
            GenericRecordProperties.ComputedUSt,

            GenericRecordProperties.RecordBelegfeld1,
            GenericRecordProperties.ItemBelegfeld2,

            GenericRecordProperties.ComputedKABruttoSoll,
            GenericRecordProperties.ComputedKABruttoHaben,
            GenericRecordProperties.ComputedKASaldoBrutto,
            GenericRecordProperties.ComputedNetto,

            GenericRecordProperties.ComputedKASoll,
            GenericRecordProperties.ComputedKAHaben,
            GenericRecordProperties.ComputedSaldo,

            GenericRecordProperties.RecordCurrencyCode,
            GenericRecordProperties.RecordCurrencyOriginalAmount,
            GenericRecordProperties.RecordCurrencyRate,

            GenericRecordProperties.ComputedZA,
            GenericRecordProperties.ComputedStatus,
            GenericRecordProperties.ItemKS,
            GenericRecordProperties.ItemBuchungstext,
            GenericRecordProperties.ItemBuchungstext2,
            GenericRecordProperties.RecordDocuments,
            GenericRecordProperties.RecordXRechnung,
            GenericRecordProperties.RecordBankingTransactionId,
            GenericRecordProperties.RecordCashRegisterTransactionId,
            GenericRecordProperties.ControlPaymentConnection,
            GenericRecordProperties.RecordNotes,
            GenericRecordProperties.RecordUStVA,
            TableProperties.ControlAction,
            GenericRecordProperties.RecordReview,
            GenericRecordProperties.RecordAiReview,
            TableProperties.ControlLog,
            TableProperties.ComputedModul,
            GenericRecordProperties.ComputedSHInfo,
        ];
    }

    nonEditableTableColumns() {
        return new Set([
            GenericRecordProperties.ComputedKABruttoSoll,
            GenericRecordProperties.ComputedKABruttoHaben,
            GenericRecordProperties.ComputedKASaldoBrutto,

            GenericRecordProperties.ComputedNetto,
            GenericRecordProperties.ComputedKASoll,
            GenericRecordProperties.ComputedKAHaben,
            GenericRecordProperties.ComputedSaldo,

            GenericRecordProperties.ItemUStPerc,
            GenericRecordProperties.ItemUSt13b,
            GenericRecordProperties.ComputedUSt,
            GenericRecordProperties.ComputedSHInfo,
        ]);
    }

    getConfig(): ProductConfig {
        return {
            ...super.getConfig(),
            useSollHabenHints: true,
        };
    }
}

export class ProductCommonAuswertung extends ProductAggregation {
    tableColumns() {
        return [
            TableProperties.ComputedNr,
            GenericRecordProperties.RecordPeriod,
            GenericRecordProperties.RecordDatum,
            GenericRecordProperties.RecordContact,
            GenericRecordProperties.RecordCategoryCreditorNum,
            GenericRecordProperties.RecordCategoryCreditorName,
            GenericRecordProperties.RecordBelegfeld1,
            GenericRecordProperties.ItemBelegfeld2,
            GenericRecordProperties.ItemCategoryCreditorNum,
            GenericRecordProperties.ItemCategoryCreditorName,
            GenericRecordProperties.RecordBrutto,
            GenericRecordProperties.RecordCurrencyCode,
            GenericRecordProperties.RecordCurrencyOriginalAmount,
            GenericRecordProperties.RecordCurrencyRate,
            GenericRecordProperties.ItemUStPerc,
            GenericRecordProperties.ItemUSt13b,
            GenericRecordProperties.ItemKS,
            GenericRecordProperties.RecordFalligkeit,
            GenericRecordProperties.ItemBuchungstext,
            GenericRecordProperties.ItemBuchungstext2,
            GenericRecordProperties.RecordDocuments,
            GenericRecordProperties.RecordXRechnung,
            GenericRecordProperties.RecordBankingTransactionId,
            GenericRecordProperties.RecordCashRegisterTransactionId,
            GenericRecordProperties.RecordReview,
            GenericRecordProperties.RecordAiReview,
            GenericRecordProperties.RecordNotes,
            GenericRecordProperties.RecordUStVA,
            // TableProperties.ControlLog,
            TableProperties.ComputedModul,
            GenericRecordProperties.ComputedSHInfo,
        ];
    }
}
